import {
  gameplayConfig,
  isModeId,
  isTimedModeId,
  type ModeId,
} from "../config/gameplay-config";
import type { RoundEndReason } from "../simulation/round-state";
import type { ProgressState } from "./progress-state";
import { progressConfig } from "../config/progress-config";
import { retainNewest } from "./runtime-stability";
import {
  isShareableChallengeIdentity,
  type ShareableChallengeIdentity,
} from "../config/challenge-link-config";

export interface RoundSettlementReceipt {
  readonly challengeIdentity?: ShareableChallengeIdentity;
  readonly coinsEarned: number;
  readonly finalLevel: number;
  readonly foodCollected: number;
  readonly id: string;
  readonly mode: ModeId;
  readonly result: RoundEndReason | null;
  readonly snakesDefeated: number;
}

export type RoundSettlementStatus = "conflict" | "duplicate" | "settled";
export type RoundSettlementQueueStatus = "conflict" | "duplicate" | "queued";

export interface RoundSettlementTransition {
  readonly state: ProgressState;
  readonly status: RoundSettlementStatus;
}

export interface RoundSettlementQueueTransition {
  readonly state: ProgressState;
  readonly status: RoundSettlementQueueStatus;
}

const isSafeCounter = (value: number): boolean =>
  Number.isSafeInteger(value) && value >= 0;

export const isRoundSettlementReceipt = (
  value: unknown,
): value is RoundSettlementReceipt => {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }
  const receipt = value as Record<string, unknown>;
  const id = receipt.id;
  const mode = receipt.mode;
  const result = receipt.result;
  const challengeIdentity = receipt.challengeIdentity;
  const validOutcome =
    (typeof mode === "string" && isTimedModeId(mode) && result === "timer") ||
    (mode === "free-play" && result === null);
  return (
    typeof id === "string" &&
    /^[A-Za-z0-9:_-]{1,128}$/u.test(id) &&
    typeof mode === "string" &&
    isModeId(mode) &&
    validOutcome &&
    (challengeIdentity === undefined ||
      (mode === "challenge-1" &&
        isShareableChallengeIdentity(challengeIdentity))) &&
    typeof receipt.coinsEarned === "number" &&
    isSafeCounter(receipt.coinsEarned) &&
    typeof receipt.finalLevel === "number" &&
    Number.isSafeInteger(receipt.finalLevel) &&
    receipt.finalLevel > 0 &&
    receipt.finalLevel <= gameplayConfig.growth.maximumLevel &&
    typeof receipt.foodCollected === "number" &&
    isSafeCounter(receipt.foodCollected) &&
    typeof receipt.snakesDefeated === "number" &&
    isSafeCounter(receipt.snakesDefeated)
  );
};

const receiptsEqual = (
  first: RoundSettlementReceipt,
  second: RoundSettlementReceipt,
): boolean =>
  first.challengeIdentity === second.challengeIdentity &&
  first.coinsEarned === second.coinsEarned &&
  first.finalLevel === second.finalLevel &&
  first.foodCollected === second.foodCollected &&
  first.mode === second.mode &&
  first.result === second.result &&
  first.snakesDefeated === second.snakesDefeated;

const addCounter = (current: number, increment: number): number =>
  Math.min(Number.MAX_SAFE_INTEGER, current + increment);

const updatePersonalBest = (
  state: ProgressState,
  receipt: RoundSettlementReceipt,
): ProgressState["personalBestLevels"] =>
  isTimedModeId(receipt.mode) && receipt.challengeIdentity === undefined
    ? {
        ...state.personalBestLevels,
        [receipt.mode]: Math.max(
          state.personalBestLevels[receipt.mode],
          receipt.finalLevel,
        ),
      }
    : state.personalBestLevels;

const updateDailyChallengeRecords = (
  state: ProgressState,
  receipt: RoundSettlementReceipt,
): ProgressState["dailyChallengeRecords"] => {
  const identity = receipt.challengeIdentity;
  if (identity === undefined) {
    return state.dailyChallengeRecords;
  }
  const existing = state.dailyChallengeRecords.find(
    (record) => record.identity === identity,
  );
  if (existing !== undefined) {
    return state.dailyChallengeRecords.map((record) =>
      record.identity === identity
        ? {
            ...record,
            bestLevel: Math.max(record.bestLevel, receipt.finalLevel),
          }
        : record,
    );
  }
  return retainNewest(
    [
      ...state.dailyChallengeRecords,
      { bestLevel: receipt.finalLevel, completed: true, identity },
    ],
    progressConfig.maximumDailyChallengeRecords,
  );
};

export const queueRoundSettlementProgress = (
  state: ProgressState,
  receipt: RoundSettlementReceipt,
): RoundSettlementQueueTransition => {
  if (!isRoundSettlementReceipt(receipt)) {
    throw new RangeError("Round settlement receipt is invalid");
  }
  const settled = state.settledRoundReceipts.find(
    (candidate) => candidate.id === receipt.id,
  );
  if (settled !== undefined) {
    return {
      state,
      status: receiptsEqual(settled, receipt) ? "duplicate" : "conflict",
    };
  }
  const pending = state.pendingRoundSettlementReceipts.find(
    (candidate) => candidate.id === receipt.id,
  );
  if (pending !== undefined) {
    return {
      state,
      status: receiptsEqual(pending, receipt) ? "queued" : "conflict",
    };
  }
  return {
    state: {
      ...state,
      pendingRoundSettlementReceipts: [
        ...state.pendingRoundSettlementReceipts,
        { ...receipt },
      ],
    },
    status: "queued",
  };
};

export const settleRoundProgress = (
  state: ProgressState,
  receipt: RoundSettlementReceipt,
): RoundSettlementTransition => {
  if (!isRoundSettlementReceipt(receipt)) {
    throw new RangeError("Round settlement receipt is invalid");
  }
  const prior = state.settledRoundReceipts.find(
    (candidate) => candidate.id === receipt.id,
  );
  if (prior !== undefined) {
    const status = receiptsEqual(prior, receipt) ? "duplicate" : "conflict";
    const matchingPending = state.pendingRoundSettlementReceipts.some(
      (candidate) =>
        candidate.id === receipt.id && receiptsEqual(candidate, receipt),
    );
    return {
      state:
        status === "duplicate" && matchingPending
          ? {
              ...state,
              pendingRoundSettlementReceipts:
                state.pendingRoundSettlementReceipts.filter(
                  (candidate) => candidate.id !== receipt.id,
                ),
            }
          : state,
      status,
    };
  }
  const pending = state.pendingRoundSettlementReceipts.find(
    (candidate) => candidate.id === receipt.id,
  );
  if (pending !== undefined && !receiptsEqual(pending, receipt)) {
    return { state, status: "conflict" };
  }
  return {
    state: {
      ...state,
      dailyChallengeRecords: updateDailyChallengeRecords(state, receipt),
      pendingRoundSettlementReceipts:
        state.pendingRoundSettlementReceipts.filter(
          (candidate) => candidate.id !== receipt.id,
        ),
      personalBestLevels: updatePersonalBest(state, receipt),
      settledRoundReceipts: retainNewest(
        [...state.settledRoundReceipts, { ...receipt }],
        progressConfig.maximumSettledRoundReceipts,
      ),
      statistics: {
        foodCollected: addCounter(
          state.statistics.foodCollected,
          receipt.foodCollected,
        ),
        roundsPlayed: addCounter(
          state.statistics.roundsPlayed,
          isTimedModeId(receipt.mode) ? 1 : 0,
        ),
        snakesDefeated: addCounter(
          state.statistics.snakesDefeated,
          receipt.snakesDefeated,
        ),
      },
      totalCoins: addCounter(state.totalCoins, receipt.coinsEarned),
    },
    status: "settled",
  };
};
